/**
 * A clip recorded in the browser (V1.1-EB-T02): the microphone through the test call's audio engine (16 kHz mono,
 * 20 ms frames), kept in memory and handed over as a WAV, which the API reads as any upload. The browser's own
 * recorder makes WebM, which the platform does not read.
 */
import type { AudioEngine } from "../agents/test/audio/engine";

export const RATE = 16000;
export const MAX_SECONDS = 120;

/** 16-bit mono PCM frames as a WAV file. */
export function wavOf(frames: ArrayBuffer[], rate = RATE): Blob {
  const bytes = frames.reduce((n, f) => n + f.byteLength, 0);
  const header = new ArrayBuffer(44);
  const v = new DataView(header);
  const write = (at: number, s: string) => [...s].forEach((c, i) => v.setUint8(at + i, c.charCodeAt(0)));
  write(0, "RIFF"); v.setUint32(4, 36 + bytes, true); write(8, "WAVE");
  write(12, "fmt "); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, rate, true); v.setUint32(28, rate * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  write(36, "data"); v.setUint32(40, bytes, true);
  return new Blob([header, ...frames], { type: "audio/wav" });
}

export interface Recording {
  /** Seconds recorded so far. */
  seconds(): number;
  /** Stop: the recording as a WAV (stopped by itself at MAX_SECONDS). */
  stop(): Promise<Blob>;
}

/** Ask for the microphone and start recording. The engine is loaded only when asked for. */
export async function startRecording(onFull: () => void): Promise<Recording> {
  const { createAudioEngine } = await import("../agents/test/audio/engine");
  const engine: AudioEngine = createAudioEngine();
  const frames: ArrayBuffer[] = [];
  let bytes = 0;
  const limit = MAX_SECONDS * RATE * 2;
  await engine.start((frame) => {
    if (bytes >= limit) return;
    frames.push(frame);
    bytes += frame.byteLength;
    if (bytes >= limit) onFull();
  });
  return {
    seconds: () => bytes / (RATE * 2),
    async stop() {
      await engine.stop();
      return wavOf(frames);
    },
  };
}
